import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _has from "lodash/has";
import _isUndefined from "lodash/isUndefined";
var _excluded = ["noClearFix", "strictChecks"];
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import StructuredContent from '@nerdwallet/structured-content-core/structured-content';
import { transform } from '@nerdwallet/structured-content-core/tree-utils';
import baseStyles from '@nerdwallet/base-styles/react';
import Container from '@nerdwallet/react-container';
import { NativeType as Type } from '@nerdwallet/currency';
import { AnchorContextProvider } from './contexts/AnchorContext';
import { CitationContextProvider } from './contexts/CitationContext';
import { MarketplaceContextProvider } from './contexts/MarketplaceContext';
import { PrimitiveProvider } from './contexts/PrimitiveContext';
import { FilterContextProvider } from './contexts/FilterContext';
import schema from './schema';

/**
 * StructuredContentWrapper Component
 * - default renders Primitives in <Type />
 * - wraps core StructuredContent in a clearfix Container unless noClearFix prop is set
 * - wraps core StructuredContent with AnchorContextProvider
 *   - this is to scope all AnchorLink/AnchorLinkTarget types to only this document by default
 *   - use <AnchorContextProvider> in your application to tie together AnchorLink/AnchorLinkTargets in multiple documents
 * - wraps core StructuredContent with MarketplaceContextProvider
 *   - this is to provide access to the marketplace context to grab loaded marketplace entities for OODMessage
 * - when strictChecks are set it throws error when it finds the components are not explicitly defined on schema or are undefined inside the document
 */
var StructuredContentWrapper = function StructuredContentWrapper(_ref) {
  var noClearFix = _ref.noClearFix,
      strictChecks = _ref.strictChecks,
      props = _objectWithoutProperties(_ref, _excluded);

  var scProps = useMemo(function () {
    if (strictChecks) {
      return _objectSpread({}, props);
    }

    var children = transform(props.children, schema, function (node) {
      if (_isUndefined(node) || _has(node, 'type') && !_has(schema, node.type)) {
        return null;
      }

      return node;
    });
    return _objectSpread(_objectSpread({}, props), {}, {
      children: children
    });
  }, [props, strictChecks]); // We don't have a root node. Return null here to prevent StructuredContent from throwing

  if (!scProps.children) return null;
  var children = /*#__PURE__*/React.createElement(AnchorContextProvider, null, /*#__PURE__*/React.createElement(FilterContextProvider, null, /*#__PURE__*/React.createElement(CitationContextProvider, null, /*#__PURE__*/React.createElement(MarketplaceContextProvider, null, /*#__PURE__*/React.createElement(PrimitiveProvider, {
    Primitive: props.Primitive
  }, /*#__PURE__*/React.createElement(StructuredContent, scProps))))));
  if (noClearFix) return children;
  var containerProps = baseStyles('clearfix');
  return /*#__PURE__*/React.createElement(Container, containerProps, children);
};

StructuredContentWrapper.propTypes = _objectSpread(_objectSpread({}, StructuredContent.propTypes), {}, {
  noClearFix: PropTypes.bool,
  strictChecks: PropTypes.bool
});
StructuredContentWrapper.defaultProps = _objectSpread(_objectSpread({}, StructuredContent.defaultProps), {}, {
  Primitive: Type,
  noClearFix: false,
  strictChecks: false
});
export default StructuredContentWrapper;